/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useRef } from 'react';
import useTracking from 'src/hooks/useTracking';
import { DatahubEntity, WatchtimeAction } from 'src/interfaces/Datahub';
import { EVENT_TYPES } from 'src/interfaces/enums';
import ApplicationService from 'src/services/ApplicationService';
import PlayerService from 'src/services/PlayerService';

const TRACK_INTERVAL_SECONDS = 10;
const TRACK_THRESHOLD_SECONDS = 3;

function useTrackWatchTime() {
  const watchtimeStartTime = useRef(0);
  const isSeeking = useRef(false);
  const { trackDatahub } = useTracking();

  const updateWatchtimeStartTime = useCallback((player: PlayerService) => {
    const currentTime = Math.floor(player.currentTime);
    if (watchtimeStartTime.current >= currentTime) {
      watchtimeStartTime.current = currentTime;
    } else {
      watchtimeStartTime.current = currentTime + 1;
    }
  }, []);

  const trackWatchtime = useCallback(
    (player: PlayerService) => {
      const startTime = Math.floor(watchtimeStartTime.current);
      const endTime = Math.floor(player.currentTime);
      const timeInterval = endTime - startTime;
      if (timeInterval < TRACK_THRESHOLD_SECONDS && !player.hasEnded) return;

      trackDatahub(DatahubEntity.WATCHTIME, WatchtimeAction.MEASURE, { startTime, endTime });
    },
    [trackDatahub],
  );

  const handleTimeUpdate = useCallback(
    (player: PlayerService) => {
      if (isSeeking.current) return;
      const currentTime = Math.floor(player.currentTime);
      const startTime = Math.floor(watchtimeStartTime.current);
      const playbackRate = player.playbackRate || 1;
      const trackInterval = TRACK_INTERVAL_SECONDS * playbackRate;
      const secondsCount = currentTime - startTime;

      if (secondsCount >= trackInterval - 1) {
        trackWatchtime(player);
        updateWatchtimeStartTime(player);
      }
    },
    [trackWatchtime, updateWatchtimeStartTime],
  );

  const handleSeekInit = useCallback(
    (player: PlayerService) => {
      isSeeking.current = true;
      if (player.paused()) return;
      trackWatchtime(player);
    },
    [trackWatchtime],
  );

  const handleSeekEnd = useCallback(
    (player: PlayerService) => {
      isSeeking.current = false;
      updateWatchtimeStartTime(player);
    },
    [updateWatchtimeStartTime],
  );

  const handlePause = useCallback(
    (player: PlayerService) => {
      trackWatchtime(player);
    },
    [trackWatchtime],
  );

  const handlePlay = useCallback(
    (player: PlayerService) => {
      const startTime = Math.floor(watchtimeStartTime.current);
      const currentTime = Math.floor(player.currentTime);
      const timeInterval = currentTime - startTime;
      if (timeInterval >= TRACK_THRESHOLD_SECONDS || currentTime < 1) {
        updateWatchtimeStartTime(player);
      }
    },
    [updateWatchtimeStartTime],
  );

  const createWatchtimeListeners = useCallback(() => {
    const player = ApplicationService.player;
    if (!player) return;

    player.addEventListener(EVENT_TYPES.TIME_UPDATE, () => handleTimeUpdate(player));

    player.addEventListener(EVENT_TYPES.SEEK_INIT, () => handleSeekInit(player));

    player.addEventListener(EVENT_TYPES.SEEK_ENDED, () => handleSeekEnd(player));

    player.addEventListener(EVENT_TYPES.PAUSE, () => handlePause(player));

    player.addEventListener(EVENT_TYPES.PLAY, () => handlePlay(player));

    player.addEventListener(EVENT_TYPES.FIRST_PLAY, () => updateWatchtimeStartTime(player));
  }, [handleTimeUpdate, handleSeekInit, handleSeekEnd, handlePause, handlePlay, updateWatchtimeStartTime]);

  return {
    createWatchtimeListeners,
  };
}

export default useTrackWatchTime;
